// App shell: brand drawer (menu navigation) + sticky app bar + content outlet.
// Mobile-first — on a phone the menu is the BOTTOM bar (bottomnav.js); from md up the drawer is a
// static sidebar. In between the drawer still slides in over a scrim, opened from the bar's "More".
import { html, signal, computed, navigate } from '../shared/t4.js';
import { currentUser, logout, isPartnerAdmin, isPlatformAdmin, partnerRegionName } from '../shared/auth.js';
import { theme, toggleTheme } from '../shared/theme.js';
import { idleWarning, idleCountdown, startIdleWatch, stopIdleWatch, stayActive } from '../shared/idle.js';
import { bottomNav } from '../shared/bottomnav.js';
import { impersonating, impersonationBanner } from '../shared/impersonate.js';
import { versionBadge, releaseNotesModal } from '../shared/version.js';
import { icon } from '../shared/icons.js';

function signOut(){ stopIdleWatch(); logout(); navigate('/login'); }

const navOpen = signal(false);
const signingOut = signal(false);   // #3 — confirm before signing out

// Admin surfaces (plan §11). `soon` items are visible but not yet wired.
// Icon names are Lucide keys — the SVG data lives in src/shared/icons.js. Adding one to this list
// after loading the path into PATHS there gives us a consistent stroke / size / colour, no more
// Unicode-glyph-soup where every icon is a different weight.
// `partner: true` marks the screens a REGIONAL PARTNER may see (region-scoped by the API). Everything
// without it is global-only and hidden from a Regional Partner's nav (and guarded in main.js).
// `children` splits one item into sub-items: Ledger categories → its two lists, IFRS (the global one, the
// item's own key and path) and GAAP (each country's own; owner 2026-10-20).
export const NAV = [
  { section:'Overview', items:[
      { key:'dashboard',  label:'Dashboard',         icon:'layout-dashboard', path:'/', partner:true },
      { key:'bugs',       label:'Bugs',              icon:'bug',              path:'/bugs' },
  ]},
  { section:'Master admin', items:[
      { key:'administrators', label:'Administrators', icon:'shield-check',    path:'/administrators' },
      { key:'categories', label:'Ledger categories', icon:'layers',           path:'/categories', children:[
          { key:'categories',      label:'IFRS', path:'/categories' },
          { key:'categories-gaap', label:'GAAP', path:'/categories/gaap' },
      ]},
      { key:'countries',  label:'Countries',         icon:'globe',            path:'/countries' },
      { key:'languages',  label:'Languages',         icon:'languages',        path:'/languages' },
      { key:'forex',      label:'Forex',             icon:'arrow-left-right', path:'/forex' },
  ]},
  { section:'Tenancy', items:[
      { key:'companies',  label:'Companies',         icon:'building-2',       path:'/companies', partner:true },
      { key:'users',      label:'Users',             icon:'users',            path:'/users', partner:true },
      { key:'billing',    label:'Billing',           icon:'credit-card',      path:'/billing', partner:true },
      { key:'partners',   label:'Partners',          icon:'handshake',        path:'/partners' },
  ]},
];

// A Regional Partner sees only the `partner:true` items, in their section (empty sections dropped).
export function navGroupsFor(){
  if(isPartnerAdmin() && !isPlatformAdmin()){
    return NAV.map(g => ({ ...g, items: g.items.filter(it => it.partner) })).filter(g => g.items.length);
  }
  return NAV;
}

function go(item){
  if(item.soon) return;
  navOpen.value = false;
  navigate(item.path);
}

// The item a screen's key belongs to: a sub-item's parent (GAAP → Ledger categories), else the key itself —
// so the phone bar's "Ledgers" tab is lit on both lists.
export function navParentKey(key){
  for(const group of NAV) for(const it of group.items)
    if(it.children && it.children.some(ch => ch.key === key)) return it.key;
  return key;
}

const navItem = (it, activeKey) => html`
  <div class=${'nav-item' + (activeKey === it.key ? ' active' : '') + (it.soon ? ' soon' : '')}
       @click=${() => go(it)}>
    <span class="nav-ico">${icon(it.icon)}</span>
    <span class="nav-lbl">${it.label}</span>
    ${it.soon ? html`<span class="badge badge-light soon-badge">soon</span>` : ''}
  </div>`;

// An item with sub-items: its own row (opens the first sub-item) and the sub-items indented under it.
const navParent = (it, activeKey) => html`
  <div class=${'nav-item nav-parent' + (it.children.some(ch => ch.key === activeKey) ? ' open' : '')}
       @click=${() => go(it)}>
    <span class="nav-ico">${icon(it.icon)}</span>
    <span class="nav-lbl">${it.label}</span>
  </div>
  ${it.children.map(ch => html`
    <div class=${'nav-item nav-sub' + (activeKey === ch.key ? ' active' : '')} @click=${() => go(ch)}>
      <span class="nav-lbl">${ch.label}</span>
    </div>`)}`;

// The mobile bottom bar. A phone bar holds ~5 targets before they stop being tappable, so it
// carries the four built screens an admin actually lives in, plus "More" which opens the same
// drawer the desktop shows — nothing in NAV becomes unreachable, it is one tap deeper.
// Short labels because a tab is exactly 75px wide at 375px — "Dashboard" measured as an ellipsised
// "Dashboa…" there, so the console root is "Home" (which is also what the tenant bar calls theirs).
export const BOTTOM_NAV = [
  { key:'dashboard',      label:'Home',      icon:'layout-dashboard', path:'/' },
  { key:'administrators', label:'Admins',    icon:'shield-check',     path:'/administrators' },
  { key:'categories',     label:'Ledgers',   icon:'layers',           path:'/categories' },
  { key:'countries',      label:'Countries', icon:'globe',            path:'/countries' },
];

export function adminNavItems(){
  // A Regional Partner's phone bar shows only their scoped screens; the drawer ("More") is likewise
  // filtered by navGroupsFor().
  const bar = (isPartnerAdmin() && !isPlatformAdmin())
    ? [{ key:'dashboard', label:'Home', icon:'layout-dashboard', path:'/' },
       { key:'companies', label:'Companies', icon:'building-2', path:'/companies' },
       { key:'users', label:'Users', icon:'users', path:'/users' },
       { key:'billing', label:'Billing', icon:'credit-card', path:'/billing' }]
    : BOTTOM_NAV;
  return [...bar,
    { key:'more', label:'More', icon:'more-horizontal', onClick: () => { navOpen.value = true; } }];
}

export function shell(activeKey, title, content){
  document.body.classList.remove('vt');   // admin surface keeps the aboo skin (vt.css is tenant-only)
  // imp-on shifts the fixed drawer + sticky bar under the support-access banner — an impersonating
  // admin is routed to the tenant app (main.js guard), but the banner still covers any transition.
  const layoutClass = computed(() => 'layout' + (navOpen.value ? ' nav-open' : '')
    + (impersonating.value ? ' imp-on' : ''));
  startIdleWatch(signOut);   // #1 — idempotent; begins on the first authed screen
  const regional = isPartnerAdmin() && !isPlatformAdmin();   // Regional Partner — scoped console
  const region = regional ? (partnerRegionName() || 'Region') : '';
  return html`
    <div class=${layoutClass}>
      <div class="scrim" @click=${() => { navOpen.value = false; }}></div>

      <aside class="drawer">
        <div class="brand-lockup drawer-brand">
          <span class="brand-dot"></span><span class="brand-name">aboo</span><span class="brand-sub">${regional ? 'regional partner' : 'admin'}</span>
        </div>
        <nav class="nav-list">
          ${navGroupsFor().map(group => html`
            <div class="nav-section">${group.section}</div>
            ${group.items.map(it => it.children ? navParent(it, activeKey) : navItem(it, activeKey))}
          `)}
        </nav>
        <div class="drawer-foot">${versionBadge('long')}</div>
      </aside>

      <div class="main">
        <header class="app-bar">
          <div class="app-title">${title}</div>
          <div class="app-user">
            <button class="theme-toggle" title="Toggle light / dark" @click=${() => toggleTheme()}>${() => theme.value === 'dark' ? icon('sun', {size: 18}) : icon('moon', {size: 18})}</button>
            ${regional ? html`<span class="region-chip" title="Your console is locked to this region">${icon('lock', {size: 13})} ${region}</span>` : ''}
            <span class="user-name">${currentUser}</span>
            ${() => signingOut.value
              ? html`<span class="confirm-inline"><span class="confirm-q">Sign out?</span>
                  <button type="button" class="btn-confirm-yes" @click=${() => { signingOut.value = false; signOut(); }}>Yes</button>
                  <button type="button" class="btn-confirm-no" @click=${() => { signingOut.value = false; }}>No</button></span>`
              : html`<button class="btn btn-sm btn-outline" @click=${() => { signingOut.value = true; }}>Sign out</button>`}
          </div>
        </header>
        <div class="content">${content}</div>
      </div>

      ${bottomNav(adminNavItems(), navParentKey(activeKey))}
      ${impersonationBanner()}
      ${releaseNotesModal()}

      ${() => idleWarning.value ? html`
        <div class="modal-overlay idle-overlay">
          <div class="modal-card idle-card">
            <div class="modal-head"><h3>Still there?</h3></div>
            <p class="idle-msg">You've been inactive for a while. For your security we'll sign you out
              in <b>${idleCountdown}</b> second${() => idleCountdown.value === 1 ? '' : 's'}.</p>
            <div class="form-actions">
              <button type="button" class="btn btn-outline" @click=${signOut}>Sign out now</button>
              <button type="button" class="btn btn-primary" @click=${() => stayActive()}>Stay signed in</button>
            </div>
          </div>
        </div>` : ''}
    </div>`;
}
